// Corpus-free category definitions — safe for client bundles.
export type NaatiFaqCategory =
  | "basics"
  | "civil-registry"
  | "education"
  | "financial"
  | "partner-visa"
  | "student-work-visa"
  | "online-digital"
  | "pricing-turnaround"
  | "coverage-area"
  | "other-countries";

export interface NaatiCategoryDef {
  slug: NaatiFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const NAATI_FAQ_CATEGORIES: NaatiCategoryDef[] = [
  {
    slug: "basics",
    titleTh: "พื้นฐาน NAATI คืออะไร",
    titleEn: "NAATI Basics",
    descTh: "NAATI คืออะไร นักแปล CPN คืออะไร ต่างจากรับรองแบบอื่นอย่างไร และหน่วยงานไหนยอมรับบ้าง",
    descEn: "What NAATI is, what a CPN-credentialed translator means, and which authorities accept it.",
    matchTh: /(naati คือ|คืออะไร|cpn|practitioner|ตราประทับ naati|ตรวจสอบนักแปล|หมายเลข naati|หมดอายุ|ต่ออายุ|มีใบอนุญาต|ไดเรกทอรี)/i,
    matchEn: /(what is naati|cpn|practitioner number|credential|stamp|expire|renew|directory|licen[cs]ed)/i,
  },
  {
    slug: "civil-registry",
    titleTh: "เอกสารทะเบียนราษฎร",
    titleEn: "Civil Registry Documents",
    descTh: "สูติบัตร ทะเบียนบ้าน ทะเบียนสมรส ใบหย่า ใบเปลี่ยนชื่อ บัตรประชาชน ใบมรณบัตร",
    descEn: "Birth certificates, house registration, marriage/divorce certs, name changes, ID cards, death certs.",
    matchTh: /(สูติบัตร|ใบเกิด|ทะเบียนบ้าน|บัตรประชาชน|ทะเบียนสมรส|ใบสมรส|ใบหย่า|เปลี่ยนชื่อ|เปลี่ยนนามสกุล|คำนำหน้าชื่อ|มรณบัตร|โสด|สด\.43|ผ่านเกณฑ์ทหาร|รับบุตรบุญธรรม|สิทธิปกครองบุตร|พินัยกรรม|บันทึกประจำวันตำรวจ|ประวัติอาชญากรรม|ใบขับขี่)/,
    matchEn: /(birth certificate|house registration|id card|marriage cert|divorce|name change|death certificate|single status|adoption|custody|will|police report|criminal record|driver'?s? licen[cs]e)/i,
  },
  {
    slug: "education",
    titleTh: "เอกสารการศึกษา",
    titleEn: "Education Documents",
    descTh: "วุฒิการศึกษา ทรานสคริปต์ ใบปริญญา ใบ รบ. หนังสือรับรองสภาพนิสิต และเอกสารยื่นสมัครเรียนต่อ",
    descEn: "Degrees, transcripts, graduation certificates, school records and study-visa academic paperwork.",
    matchTh: /(วุฒิการศึกษา|ทรานสคริปต์|ใบปริญญา|ใบ รบ\.|สภาพนิสิต|เรียนต่อ|วีซ่านักเรียน|ประกอบวิชาชีพครู)/,
    matchEn: /(transcript|degree|graduat|academic record|school record|student visa)/i,
  },
  {
    slug: "financial",
    titleTh: "เอกสารการเงินและบริษัท",
    titleEn: "Financial & Corporate Documents",
    descTh: "สมุดบัญชี bank statement สลิปเงินเดือน ภาษี งบการเงิน สัญญา โฉนดที่ดิน เอกสารบริษัท",
    descEn: "Bank statements, payslips, tax filings, financials, contracts, land title deeds and corporate paperwork.",
    matchTh: /(bank statement|สมุดบัญชี|สลิปเงินเดือน|เงินเดือน|ภาษี|ภ\.ง\.ด|งบการเงิน|สัญญา|โฉนด|บริษัท|นิติบุคคล|บอจ|ทะเบียนการค้า|ทะเบียนพาณิชย์|ประกันสังคม|ผู้สอบบัญชี|ใบเสร็จ|ใบแจ้งหนี้|จำนอง|กู้ยืม)/,
    matchEn: /(bank statement|payslip|salary|tax|financial statement|contract|land title|deed|company|corporate|affidavit|invoice|mortgage|loan)/i,
  },
  {
    slug: "partner-visa",
    titleTh: "วีซ่าคู่ครอง / ความสัมพันธ์",
    titleEn: "Partner / Relationship Visa",
    descTh: "วีซ่าคู่หมั้น คู่สมรส หลักฐานความสัมพันธ์ แชทไลน์ รูปถ่าย และวีซ่าติดตามครอบครัว",
    descEn: "Partner/fiancé visas, relationship evidence, chat logs, photos and family/dependent visas.",
    matchTh: /(วีซ่าคู่หมั้น|วีซ่าแต่งงาน|วีซ่าคู่สมรส|วีซ่าติดตาม|หลักฐานความสัมพันธ์|แชทไลน์|facebook chat|whatsapp|รูปถ่ายความสัมพันธ์|จดหมายอธิบายความสัมพันธ์|วีซ่าพ่อแม่|วีซ่าลูก)/,
    matchEn: /(partner visa|fianc|spouse visa|relationship evidence|chat log|dependent visa|parent visa|de facto)/i,
  },
  {
    slug: "student-work-visa",
    titleTh: "วีซ่านักเรียน / ทำงาน / ทักษะ",
    titleEn: "Student / Work / Skilled Visa",
    descTh: "วีซ่านักเรียน วีซ่าทักษะ วีซ่าทำงาน การประเมินทักษะ VETASSESS TRA AHPRA ACS และ PR",
    descEn: "Student, skilled and work visas, skills assessment bodies (VETASSESS, TRA, AHPRA, ACS) and PR pathways.",
    matchTh: /(วีซ่านักเรียน|วีซ่าทักษะ|วีซ่าทำงาน|วีซ่านักลงทุน|ขอ pr|vetassess|\btra\b|ahpra|cpa australia|\bacs\b|สภาวิศวกร|ใบประกอบโรคศิลปะ|พยาบาล|ยื่นอุทธรณ์วีซ่า|home affairs|department of home affairs|สัญชาติออสเตรเลีย)/i,
    matchEn: /(student visa|skilled visa|work visa|skill assessment|vetassess|\btra\b|ahpra|cpa australia|\bacs\b|engineer(?:s|ing)? australia|nursing|permanent residency|\bpr\b|home affairs|citizenship)/i,
  },
  {
    slug: "online-digital",
    titleTh: "แปลออนไลน์ / ตราประทับดิจิทัล",
    titleEn: "Online / Digital Stamp Translation",
    descTh: "ส่งไฟล์ PDF แปลออนไลน์ ตราประทับดิจิทัล อัปโหลด ImmiAccount และไฟล์เข้ารหัส",
    descEn: "PDF uploads, digital-stamp translation, ImmiAccount file requirements and encrypted delivery.",
    matchTh: /(ออนไลน์|ไฟล์ pdf|ดิจิทัล|อิเล็กทรอนิกส์|imm[ia]account|เข้ารหัส|ส่งไฟล์)/i,
    matchEn: /(online|digital stamp|\bpdf\b|electronic|immiaccount|encrypt|upload)/i,
  },
  {
    slug: "pricing-turnaround",
    titleTh: "ราคาและระยะเวลา",
    titleEn: "Pricing & Turnaround",
    descTh: "ค่าแปล NAATI หน้าละเท่าไหร่ ราคาประหยัด งานด่วน ใช้เวลากี่วัน และค่าธรรมเนียมเพิ่มเติม",
    descEn: "NAATI translation fees per page, express turnaround, discounts and add-on charges.",
    matchTh: /(ราคา|ค่าแปล|กี่วัน|ใช้เวลา|ด่วน|ประหยัด|ค่าธรรมเนียม|24 ชั่วโมง|วันเสาร์อาทิตย์|นอกเวลาทำการ)/,
    matchEn: /(price|fee|cost|how much|how long|turnaround|express|discount|24.hour|weekend|after hours)/i,
  },
  {
    slug: "coverage-area",
    titleTh: "พื้นที่ให้บริการทั่วไทย",
    titleEn: "Service Coverage Areas",
    descTh: "รับแปล NAATI กรุงเทพ ขอนแก่น อุดรธานี ภูเก็ต เชียงใหม่ พัทยา หนองคาย และส่งเอกสารทั่วประเทศ",
    descEn: "NAATI translation service across Bangkok, Khon Kaen, Udon Thani, Phuket, Chiang Mai, Pattaya and nationwide delivery.",
    matchTh: /(กรุงเทพ|ขอนแก่น|อุดรธานี|ภูเก็ต|เชียงใหม่|พัทยา|หนองคาย|โคราช|สมุย|หาดใหญ่|ระยอง|สุขุมวิท|สีลม|อโศก|ถลาง|ใกล้ฉัน|ส่งไปรษณีย์|ส่ง ems|ส่งทั่วไทย|isan|nong khai)/i,
    matchEn: /(bangkok|khon kaen|udon thani|phuket|chiang mai|pattaya|nong khai|korat|samui|hat yai|rayong|near me|nationwide|isan|courier)/i,
  },
  {
    slug: "other-countries",
    titleTh: "นิวซีแลนด์ / แคนาดา / UK",
    titleEn: "New Zealand / Canada / UK",
    descTh: "แปล NAATI ยื่น Immigration NZ, INZ, IRCC แคนาดา และ UK Home Office",
    descEn: "Using NAATI translations for Immigration NZ (INZ), IRCC Canada and the UK Home Office.",
    matchTh: /(นิวซีแลนด์|\binz\b|immigration nz|แคนาดา|\bircc\b|\buk\b|home office|อังกฤษ)/i,
    matchEn: /(new zealand|\binz\b|immigration nz|canada|\bircc\b|\buk\b|home office|united kingdom)/i,
  },
];

export function getNaatiCategoryDef(slug: string): NaatiCategoryDef | undefined {
  return NAATI_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
